import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { SponsorTile, TILE_WIDTH } from "@/pages/sponsors/SponsorWall";
import { stripSponsorTiers } from "@/pages/sponsors/data";
import { useReducedMotionPref } from "@/hooks/useReducedMotionPref";

/**
 * Site-wide sponsor logo row, rendered by PublicLayout just above the footer.
 *
 * PublicLayout wraps EVERY route — including /login and /dashboard/* — so this
 * is restricted to the public marketing pages. Sponsor logos on a signed-in
 * dashboard or an auth form would be noise.
 *
 * /registration is feature-gated, not auth-gated, so it renders for logged-out
 * visitors and counts as a marketing surface.
 *
 * Sponsors only — the ORGANISERS group is deliberately absent here (client
 * request); they're credited on /sponsors. See stripSponsorTiers in
 * pages/sponsors/data.ts.
 *
 * Logo sizes step down by tier here exactly as they do on the wall: the tiles
 * are one fixed size (TILE_WIDTH.compact) and SponsorTile scales the mark
 * inside from the tier it is given. That keeps this row's track width — and so
 * its overflow, chevron and fade thresholds below — independent of the ladder.
 *
 * The row is STATIC and FINITE, at client request: it does not auto-scroll and
 * the logos are never duplicated. It centres when it fits (desktop today) and
 * becomes an ordinary horizontal scroller when it doesn't (mobile today, wider
 * screens once more sponsors are signed). It reaches a real start and a real
 * end — this is explicitly NOT the infinite marquee it replaced, so don't
 * reintroduce drift, ghost copies or wrap-around.
 */
const STRIP_PATHS = new Set([
  "/",
  "/sponsorship",
  "/sponsors",
  "/race-details",
  "/registration",
  "/contact",
  "/accessibility"
]);

/** Fraction of the visible width one chevron press advances. */
const STEP_RATIO = 0.8;

/**
 * Slack, in px, on the edge comparisons. `scrollLeft` is fractional and
 * `scrollLeft + clientWidth` rarely lands exactly on `scrollWidth`, so an
 * equality test would leave the "next" button live at a position it can no
 * longer move away from.
 */
const EDGE_TOLERANCE = 1;

const CONTROL_BTN =
  "inline-flex h-11 w-11 items-center justify-center rounded-md border border-border " +
  "text-foreground/80 transition-colors hover:bg-primary/10 hover:text-primary " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
  // pointer-events-none as well as opacity: a disabled button still matches
  // :hover, so without it the dead control lights up under the cursor.
  "disabled:pointer-events-none disabled:opacity-40";

type Edges = { overflowing: boolean; atStart: boolean; atEnd: boolean };

function readEdges(el: HTMLElement): Edges {
  const max = el.scrollWidth - el.clientWidth;
  return {
    overflowing: max > EDGE_TOLERANCE,
    atStart: el.scrollLeft <= EDGE_TOLERANCE,
    atEnd: el.scrollLeft >= max - EDGE_TOLERANCE
  };
}

export function SponsorStrip() {
  const { pathname } = useLocation();

  // Tolerate a trailing slash — the prerendered routes are served from /x/.
  const normalized =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

  if (!STRIP_PATHS.has(normalized) || stripSponsorTiers.length === 0) return null;

  // Split so the observers never mount on /login or /dashboard/*, and so the
  // gate above can early-return before any hooks.
  return <SponsorStripRow showCta={normalized !== "/sponsors"} />;
}

function SponsorStripRow({ showCta }: { showCta: boolean }) {
  const { t } = useTranslation("sponsors");
  // Covers BOTH the OS setting and the in-app toggle — framer's
  // useReducedMotion() alone would miss the latter. See the hook's docblock.
  const reduce = useReducedMotionPref();

  const scrollerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Starts "not overflowing": that's the desktop case and, more importantly,
  // what the prerenderer bakes — so the static HTML carries no dead controls.
  const [edges, setEdges] = useState<Edges>({
    overflowing: false,
    atStart: true,
    atEnd: true
  });

  const sync = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const next = readEdges(el);
    // Bail on an unchanged read — this fires on every scroll frame.
    setEdges((prev) =>
      prev.overflowing === next.overflowing &&
      prev.atStart === next.atStart &&
      prev.atEnd === next.atEnd
        ? prev
        : next
    );
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    const track = trackRef.current;
    if (!el || !track) return;

    sync();
    el.addEventListener("scroll", sync, { passive: true });
    /*
     * Observing the track as well as the scroller is safe here (it was a
     * feedback loop in the marquee, which resized the track in response):
     * nothing this component does changes the track's width. It's needed
     * because a late webfont reflows the tier labels, which are what set the
     * track width when a group's label is wider than its logos.
     */
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    ro.observe(track);
    return () => {
      el.removeEventListener("scroll", sync);
      ro.disconnect();
    };
  }, [sync]);

  const step = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir * Math.round(el.clientWidth * STEP_RATIO),
      behavior: reduce ? "auto" : "smooth"
    });
  };

  return (
    <section
      /* bg-card (pure white), NOT a tint. The logos have no cards any more and
         6 of the 17 files on the strip carry a hard white matte, so a tinted
         band would paint white rectangles behind them. Same constraint as
         WALL_SURFACE in SponsorWall.tsx — read that comment before changing
         this. border-t is what separates the band from the page now that the
         tint is gone. */
      className="border-t border-border bg-card"
      aria-labelledby="sponsor-strip-heading"
    >
      <div className="container py-10 md:py-12">
        <p
          id="sponsor-strip-heading"
          className="text-center text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold"
        >
          {t("strip.eyebrow")}
        </p>

        <div className="relative mt-6">
          <div
            ref={scrollerRef}
            role="region"
            aria-label={t("strip.region")}
            tabIndex={0}
            /*
             * py-2: overflow-x forces computed overflow-y to auto, which clips
             * descendants vertically — without it the tiles' hover lift and
             * their ring-offset focus ring get sheared off. Vertical only:
             * horizontal padding would land in scrollWidth.
             * overscroll-x-none stops a swipe chaining to the browser (iOS
             * back-nav). Never add snap-x: mandatory snapping re-targets after
             * every programmatic scroll and fights scrollBy.
             */
            className="scrollbar-hide overflow-x-auto overscroll-x-none rounded-md py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {/*
             * w-max + mx-auto, NOT justify-center on the scroller: auto margins
             * centre the track while it's narrower than the scroller and
             * resolve to 0 once it overflows, so the start stays reachable.
             * `justify-content: center` clips the leading edge of overflowing
             * content, and this build targets safari14.
             */}
            <div ref={trackRef} className="mx-auto flex w-max items-start gap-6 md:gap-8">
              {stripSponsorTiers.map((tier) => {
                const tierLabel = t(`tiers.${tier.id}`);
                return (
                  <div key={tier.id} className="flex shrink-0 flex-col items-center">
                    {/* nowrap: a label wrapping to two lines would push only its
                        own group's logos down and break the shared baseline. */}
                    <p className="whitespace-nowrap text-[11px] uppercase tracking-[0.2em] text-muted-foreground/80 font-semibold">
                      {tierLabel}
                    </p>
                    <ul role="list" aria-label={tierLabel} className="mt-3 flex items-stretch gap-3">
                      {tier.sponsors.map((sponsor) => (
                        <li key={sponsor.id} className={`${TILE_WIDTH.compact} shrink-0`}>
                          <SponsorTile sponsor={sponsor} tier={tier.id} size="compact" />
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>

          {/*
           * Fades are siblings of the scroller, not children, or they'd scroll
           * away with the content.
           *
           * from-card, matching the band. This used to be from-background back
           * when the band was muted/30 — that reasoning INVERTED when the band
           * went white for the matte fix above: a background-coloured gradient
           * now paints a pale-blue smear across white. The rule is simply that
           * the fade must start in the band's own colour.
           *
           * Each side shows only when there is actually content hidden that
           * way: a permanent fade over a static, fully-visible row just dims the
           * outermost logos.
           */}
          {edges.overflowing && !edges.atStart && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-card to-transparent sm:w-12"
            />
          )}
          {edges.overflowing && !edges.atEnd && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-card to-transparent sm:w-12"
            />
          )}
        </div>

        {/*
         * Controls appear only when the row actually scrolls, and sit below it
         * rather than overlaying its edges: an overlay chevron would park
         * permanently on ~44px of the first and last sponsor logo, and this row
         * exists to deliver paid logo visibility. Keyboard users can also scroll
         * the region directly — it's focusable.
         */}
        {edges.overflowing && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              className={CONTROL_BTN}
              aria-label={t("strip.prev")}
              disabled={edges.atStart}
              onClick={() => step(-1)}
            >
              <ChevronLeft aria-hidden className="h-5 w-5" />
            </button>
            <button
              type="button"
              className={CONTROL_BTN}
              aria-label={t("strip.next")}
              disabled={edges.atEnd}
              onClick={() => step(1)}
            >
              <ChevronRight aria-hidden className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* No self-link when the strip is already on the sponsors page. */}
        {showCta && (
          <div className="mt-6 text-center">
            <Link
              to="/sponsors/"
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary underline-offset-4 hover:underline"
            >
              {t("strip.cta")}
              <ArrowRight aria-hidden className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
